import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { __PDND_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseListPayloadQuarkus } from 'shell/models/http-response';
import { headCells } from '../../../../pages/notification-management/search/constants';
import {
  transformerResponse,
  transformDataset,
} from '../../../../pages/notification-management/search/utils';
import { IconButton, Typography } from '@mui/material';
import { NotificationManagementVM } from 'practice/view-models/notification-management';
import { noop } from 'shell/utils/noop';
import { Visibility } from '@mui/icons-material';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PDNDNotification } from 'shell/models/pdnd-notification';

const NotificationsTable = (): React.ReactElement => {
  const totalElementsRef = useRef<number>(0);

  const params = useParams();

  const actualPath = useLocation();

  const [tableState, setTableState] = useState<{ page: number; rowsPerPage?: number }>({
    page: 0,
  });

  const [notifications] = useHttpFetch<
    HttpResponseListPayloadQuarkus<PDNDNotification>,
    Array<NotificationManagementVM>
  >(`${__PDND_API_URL__}/notifica/list`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      params: {},
      paramsSerializer: () =>
        transformDataset({
          typeRegister: params.tipoRegistro,
          year: params.idAnnoPratica,
          fromNumber: params.progressivoPratica,
          toNumber: params.progressivoPratica,
        }),
    },
    transformer: (response) => {
      totalElementsRef.current = response.data?.pageInfo?.totalCount || 0;
      return transformerResponse(response);
    },
  });

  const count: number = useMemo(() => {
    return totalElementsRef.current || 0;
  }, [notifications.data]);

  const handleChangePage = useCallback(
    (newPage) => {
      setTableState((oldState) => ({ ...oldState, page: newPage }));
    },
    [setTableState]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      setTableState((oldState) => ({ ...oldState, page: 1, rowsPerPage: rowsPerPage }));
    },
    [setTableState]
  );

  const navigate = useNavigate();

  const onVisibilityPress = useCallback((row: NotificationManagementVM) => {
    navigate(
      generatePath(`${actualPath.pathname}/notifiche/:id`, {
        id: row.id?.toString() || '',
      })
    );
  }, []);

  const data = useMemo(() => {
    const castedData = (notifications.data as Array<NotificationManagementVM>) || [];
    return castedData.map((el) => ({
      ...el,
      action:
        el.codeState === 'CANCELLED' ? undefined : (
          <Fragment>
            <IconButton onClick={() => onVisibilityPress(el)}>
              <Visibility />
            </IconButton>
          </Fragment>
        ),
    }));
  }, [notifications.data as Array<NotificationManagementVM>]);

  return (
    <Fragment>
      {notifications.data ? (
        data?.length > 0 ? (
          <>
            <Table
              onSort={noop}
              head={headCells}
              orders={{}}
              page={tableState.page || 0}
              rowsPerPage={tableState.rowsPerPage}
              body={data || []}
              count={count || 0}
              noSort
              hideButtons
              isNotLayoutFixed
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ariaLabel="Lista Notifiche"
            />
          </>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

NotificationsTable.displayName = 'Accordion Notifications Table';

export default NotificationsTable;
